import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";

import {
  connectCustomDomain,
  loadCustomDomains,
  removeCustomDomain,
  verifyCustomDomain,
} from "../../../utils/customDomainApi";

const PRESENTATION = {
  not_configured: ["Not configured", "secondary"],
  pending_dns: ["Waiting for DNS", "warning"],
  dns_verified: ["DNS verified", "info"],
  provisioning: ["Connecting", "primary"],
  active: ["Active", "success"],
  failed: ["Needs attention", "danger"],
  removal_pending: ["Removing", "warning"],
};

const operationKey = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function dnsName(hostname) {
  const labels = String(hostname || "").split(".");
  return labels.length > 2 ? labels[0] : "@";
}

async function copy(value) {
  if (navigator.clipboard?.writeText) return navigator.clipboard.writeText(value);
  const input = document.createElement("textarea");
  input.value = value;
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  input.remove();
}

const DomainCard = ({ canEdit, instanceId, onChanged, removalPending, target }) => {
  const domain = target.domain;
  const [hostname, setHostname] = useState(domain?.hostname || "");
  const [working, setWorking] = useState(null);
  const [message, setMessage] = useState(null);
  const state = domain?.status || "not_configured";
  const [statusLabel, statusVariant] = PRESENTATION[state] || PRESENTATION.failed;
  const locked = working || removalPending || ["provisioning", "removal_pending", "active"].includes(state);
  const verified = state === "dns_verified" && hostname.trim().toLowerCase().replace(/\.$/, "") === domain?.hostname;
  const recordValue = target.dns?.value || target.dns?.values?.join(", ") || "Provided by Feradel support";

  useEffect(() => { setHostname(domain?.hostname || ""); }, [domain?.hostname]);
  useEffect(() => { if (domain?.status === "active") setMessage(null); }, [domain?.status]);

  const run = async (label, callback, successText) => {
    setWorking(label);
    setMessage(null);
    const result = await callback();
    setWorking(null);
    setMessage(result?.success
      ? { variant: "success", text: successText }
      : { variant: "danger", text: result?.error?.message || "The domain could not be updated." });
    await onChanged();
  };

  const remove = () => {
    if (!domain?.id || !window.confirm(`Remove ${domain.hostname} from ${target.label}?\n\nFeradel will update routing and issue a replacement certificate that no longer includes this hostname.`)) return;
    run("remove", () => removeCustomDomain(instanceId, domain.id, operationKey()), "The custom domain was removed.");
  };

  return (
    <article className={`custom-domain-card is-${state}`}>
      <header>
        <div><span className="custom-domain-purpose">{target.purpose}</span><h3>{target.label}</h3></div>
        <Badge bg={statusVariant}>{working ? "Working…" : statusLabel}</Badge>
      </header>
      <p>Connect a hostname you control to <strong>{target.canonicalHostname}</strong>.</p>
      <Form.Group controlId={`custom-domain-${target.key}`}>
        <Form.Label>Customer hostname</Form.Label>
        <Form.Control
          type="text"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck="false"
          placeholder={target.key === "auth" ? "login.yourfirm.com" : target.key === "matters" ? "firm.yourfirm.com" : "clients.yourfirm.com"}
          value={hostname}
          disabled={!canEdit || locked}
          onChange={event => { setHostname(event.target.value); setMessage(null); }}
        />
        <Form.Text>Enter only the hostname—no https://, path, or port.</Form.Text>
      </Form.Group>
      <div className="custom-domain-dns" aria-label={`DNS instructions for ${target.label}`}>
        <div><span>Type</span><strong>A</strong></div>
        <div><span>Name</span><strong>{dnsName(hostname || domain?.hostname)}</strong></div>
        <div><span>Value</span><strong>{recordValue}</strong></div>
        <div><span>TTL</span><strong>{target.dns?.ttl || "Automatic or 300"}</strong></div>
        <Button size="sm" variant="link" onClick={() => copy(recordValue)} disabled={!target.dns?.value}>Copy value</Button>
      </div>
      {domain?.lastError ? <Alert variant="danger" className="custom-domain-inline-alert"><strong>{domain.lastError.code}</strong><span>{domain.lastError.message}</span></Alert> : null}
      {message ? <Alert variant={message.variant} className="custom-domain-inline-alert" role="status">{message.text}</Alert> : null}
      {state === "active" ? <a className="custom-domain-active-link" href={domain.httpsUrl} target="_blank" rel="noreferrer">Open secure site ↗</a> : null}
      <footer>
        {canEdit && state !== "active" ? (
          <Button
            size="sm"
            variant="outline-primary"
            disabled={!hostname.trim() || Boolean(working) || removalPending}
            onClick={() => run("verify", () => verifyCustomDomain(instanceId, target.key, hostname.trim()), "DNS is verified. You can connect this domain now.")}
          >{working === "verify" ? <><Spinner size="sm" animation="border" /> Verifying…</> : "Verify DNS"}</Button>
        ) : null}
        {canEdit && verified ? (
          <Button
            size="sm"
            disabled={Boolean(working) || removalPending}
            onClick={() => run("connect", () => connectCustomDomain(instanceId, target.key, hostname.trim(), operationKey()), "The domain is connected and HTTPS is active.")}
          >{working === "connect" ? <><Spinner size="sm" animation="border" /> Connecting…</> : "Connect domain"}</Button>
        ) : null}
        {canEdit && domain?.id && !["provisioning", "removal_pending"].includes(state) ? <Button size="sm" variant="link" className="text-danger" onClick={remove} disabled={Boolean(working) || removalPending}>Remove</Button> : null}
      </footer>
    </article>
  );
};

export const CustomDomainsSection = ({ canEdit, instanceId, removalPending }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!instanceId) return null;
    if (!quiet) setLoading(true);
    const result = await loadCustomDomains(instanceId);
    if (result?.success) {
      setData(result);
      setError(null);
    } else setError(result?.error?.message || "Custom domains could not be loaded.");
    if (!quiet) setLoading(false);
    return result;
  }, [instanceId]);

  useEffect(() => {
    refresh();
    const timer = window.setInterval(() => {
      if (data?.targets?.some(target => ["provisioning", "removal_pending"].includes(target.domain?.status))) refresh({ quiet: true });
    }, 5000);
    return () => window.clearInterval(timer);
  }, [data?.targets, refresh]);

  const targets = useMemo(() => data?.targets || [], [data]);
  if (!instanceId || (!loading && data?.featureEnabled === false)) return null;
  return (
    <section className="custom-domains-section" aria-labelledby="custom-domains-title">
      <div className="custom-domains-heading">
        <div><span className="product-settings-eyebrow">Optional setup</span><h2 id="custom-domains-title">Custom domains</h2><p>Use your own branded hostnames for sign-in, your team workspace, and the client portal. DNS must point directly to Feradel; proxied Cloudflare records are not supported.</p></div>
        {loading ? <Spinner size="sm" animation="border" aria-label="Loading custom domains" /> : null}
      </div>
      {error ? <Alert variant="danger">{error} <Button size="sm" variant="outline-danger" onClick={() => refresh()}>Try again</Button></Alert> : null}
      <div className="custom-domain-grid">
        {targets.map(target => <DomainCard key={target.key} target={target} instanceId={instanceId} canEdit={canEdit} removalPending={removalPending} onChanged={() => refresh({ quiet: true })} />)}
      </div>
    </section>
  );
};

export default CustomDomainsSection;
